import { useToast } from '@/components/ui/use-toast';
import type { ForgotPassword } from '@edwin-group/cj-graphql/generated/graphql';
import { getForgotPasswordPage, forgotPassword, resetPassword } from '@/services/auth/auth.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as z from 'zod';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
// import { usePreFetchImage } from '@/hooks/usePreFetchImage';

const schemaForgotPassword = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

type FormDataForgotPassword = {
  email: string;
};

type FormDataResetPassword = {
  password: string;
  confirmPassword: string;
};

const schemaResetPassword = z
  .object({
    password: z.string().min(8, { message: 'Password must be at least 8 characters long' }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export default function useResetPassword() {
  const { toast } = useToast();
  const [forgotPasswordPage, setForgotPasswordPage] = useState<ForgotPassword | null>(null);
  const { register: registerForgotPassword, handleSubmit: handleSubmitForgotPassword } =
    useForm<FormDataForgotPassword>({
      resolver: zodResolver(schemaForgotPassword),
    });
  const { code } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register: registerResetPassword,
    handleSubmit: handleSubmitResetPassword,
    formState: { errors },
  } = useForm<FormDataResetPassword>({
    resolver: zodResolver(schemaResetPassword),
    mode: 'onBlur',
  });

  const onSubmitForgotPassword = async (data: FormDataForgotPassword) => {
    setIsLoading(true);
    await forgotPassword(data.email);

    toast({
      title: forgotPasswordPage?.feedback?.emailSent?.title || '',
      description: forgotPasswordPage?.feedback?.emailSent?.description?.replace('${email}', data.email) || '',
    });

    setIsLoading(false);

    trackGAEvent('reset_password_requested', {});

    await new Promise((resolve) => setTimeout(resolve, 3000));

    window.location.href = '/login';
  };

  const onSubmitResetPassword = async (data: FormDataResetPassword) => {
    setIsLoading(true);
    const { error } = await resetPassword({
      password: data.password,
      passwordConfirmation: data.confirmPassword,
      code: code || '',
    });

    if (error) {
      toast({
        title: forgotPasswordPage?.feedback?.passwordResetError?.title || 'Error resetting password',
        description: error || forgotPasswordPage?.feedback?.passwordResetError?.description,
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    toast({
      title: forgotPasswordPage?.feedback?.passwordResetSuccess?.title || 'Password reset',
      description:
        forgotPasswordPage?.feedback?.passwordResetSuccess?.description || 'Your password has been reset successfully',
    });

    setIsLoading(false);

    await new Promise((resolve) => setTimeout(resolve, 3000));

    window.location.href = '/login';
  };

  useEffect(() => {
    (async () => {
      const [forgotPasswordPageResponse] = await Promise.all([getForgotPasswordPage()]);
      setForgotPasswordPage(forgotPasswordPageResponse.page as ForgotPassword);
    })();
  }, []);

  // const { isImageLoaded } = usePreFetchImage(forgotPasswordPage?.pageImage.data?.attributes?.url);

  return {
    errors,
    forgotPasswordPage,
    handleSubmitForgotPassword: handleSubmitForgotPassword(onSubmitForgotPassword),
    registerForgotPassword,
    handleSubmitResetPassword: handleSubmitResetPassword(onSubmitResetPassword),
    registerResetPassword,
    isLoading,
    isImageLoaded: true,
  };
}
